import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sparkles, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Button } from '@/components/ui/Button';
import { Select, Label } from '@/components/ui/Input';
import { EmailTemplateEditor } from '@/components/EmailTemplateEditor';
import { EmailPreviewModal } from '@/components/EmailPreviewModal';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-error';
import { useWizardStore, type EmailTemplates } from '../lib/wizard-store';

interface EmailTemplateStyle {
  id: string;
  label: string;
  description: string;
  templates: EmailTemplates;
}

async function fetchStyles(): Promise<{ styles: EmailTemplateStyle[]; default: string }> {
  const { data } = await apiClient.get<{ styles: EmailTemplateStyle[]; default: string }>(
    '/v1/email-template-styles',
  );
  return data;
}

const W3_ERROR_CODES = {
  ai_not_configured: "L'IA n'est pas configurée.",
  llm_upstream_failed: 'Le LLM est indisponible — réessaye dans quelques secondes.',
  llm_bad_format: "L'IA a renvoyé un format inattendu — relance.",
  ERR_VALIDATION: 'Templates invalides — vérifie qu\'aucun champ n\'est vide.',
};

export function W3_EmailTemplates() {
  const store = useWizardStore();
  const {
    email_templates,
    email_template_style_id,
    patchEmailTemplates,
    setEmailTemplates,
    setEmailTemplateStyleId,
    sender_first_name,
    sender_company_name,
    sender_description,
    sender_website,
    offer_title,
    offer_description,
    offer_url,
    target_country,
    target_region,
    language,
  } = store;

  const [previewOpen, setPreviewOpen] = useState(false);
  const [adaptLoading, setAdaptLoading] = useState(false);
  const [adaptError, setAdaptError] = useState<string | null>(null);
  const [adaptNotes, setAdaptNotes] = useState<string | null>(null);

  const { data: stylesData } = useQuery({
    queryKey: ['email-template-styles'],
    queryFn: fetchStyles,
    staleTime: 60 * 60 * 1000,
  });
  const styles = stylesData?.styles ?? [];
  const currentStyle = styles.find((s) => s.id === email_template_style_id);

  function applyStyle(styleId: string) {
    const style = styles.find((s) => s.id === styleId);
    if (!style) return;
    setEmailTemplateStyleId(style.id);
    setEmailTemplates(style.templates);
    setAdaptNotes(null);
  }

  async function adaptWithAi() {
    setAdaptLoading(true);
    setAdaptError(null);
    setAdaptNotes(null);
    try {
      const { data } = await apiClient.post<{
        templates: EmailTemplates;
        notes?: string;
      }>('/v1/llm/adapt-email-templates', {
        templates: email_templates,
        styleId: email_template_style_id,
        context: {
          senderCompanyName: sender_company_name || null,
          senderDescription: sender_description || null,
          senderWebsite: sender_website || null,
          offerTitle: offer_title || null,
          offerDescription: offer_description || null,
          offerUrl: offer_url || null,
          targetCountry: target_country || null,
          targetRegion: target_region || null,
        },
        language,
      });
      setEmailTemplates(data.templates);
      setAdaptNotes(data.notes ?? null);
    } catch (err) {
      setAdaptError(getApiErrorMessage(err, W3_ERROR_CODES));
    } finally {
      setAdaptLoading(false);
    }
  }

  const campaignContext = {
    sender: sender_first_name,
    offer: offer_title,
    target: target_country,
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Templates email"
          subtitle="Choisis un style de séquence et adapte-le à ton offre. Les variables entre accolades seront remplacées per-contact au moment du push."
        />
        <CardContent className="space-y-4" data-testid="email-templates-section">
          {/* Style switcher */}
          <div className="grid grid-cols-3 gap-3 items-end" data-testid="style-picker">
            <div className="col-span-2 space-y-1">
              <Label htmlFor="template-style">Style de séquence</Label>
              <Select
                id="template-style"
                value={email_template_style_id}
                onChange={(e) => applyStyle(e.target.value)}
                data-testid="template-style-select"
                disabled={styles.length === 0}
              >
                {styles.length === 0 && <option value="original">Chargement…</option>}
                {styles.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </Select>
              {currentStyle && (
                <p className="text-xs text-gray-500" data-testid="style-description">
                  {currentStyle.description}
                </p>
              )}
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={() => applyStyle(email_template_style_id)}
              disabled={!currentStyle}
              data-testid="reset-style-btn"
              title="Restaurer les templates d'origine du style"
            >
              <RefreshCw size={14} />
              Réinitialiser
            </Button>
          </div>

          {/* Adapt with AI */}
          <div
            className="rounded-md border border-dashed border-brand-primary/40 bg-brand-primary/5 p-3 flex items-start justify-between gap-3"
            data-testid="adapt-ai-row"
          >
            <div className="text-xs text-gray-600 leading-relaxed">
              <span className="font-medium text-brand-dark">Adapter avec IA</span> — l'IA reformule
              les phrases en fonction de ton offre et de ton positionnement, en conservant le ton
              du style et tous les placeholders.
              {!offer_title && !offer_description && (
                <span className="block mt-1 text-amber-600">
                  Renseigne au moins un titre ou une description d'offre pour que l'adaptation soit utile.
                </span>
              )}
              {adaptError && (
                <span className="block mt-1 text-red-600 flex items-start gap-1">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                  {adaptError}
                </span>
              )}
              {adaptNotes && (
                <span className="block mt-1 text-gray-500 italic">Notes IA : {adaptNotes}</span>
              )}
            </div>
            <Button
              type="button"
              variant="primary"
              onClick={adaptWithAi}
              disabled={adaptLoading || (!offer_title && !offer_description)}
              data-testid="adapt-ai-btn"
            >
              {adaptLoading ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Adaptation…
                </>
              ) : (
                <>
                  <Sparkles size={14} />
                  Adapter avec IA
                </>
              )}
            </Button>
          </div>

          <Tabs defaultValue="nominative">
            <TabsList>
              <TabsTrigger value="nominative">Approche nominative</TabsTrigger>
              <TabsTrigger value="fallback">Approche fallback</TabsTrigger>
            </TabsList>

            <TabsContent value="nominative">
              <div className="space-y-6" data-testid="tab-nominative">
                <EmailTemplateEditor
                  id="email01"
                  label="Email 1 — Premier contact *"
                  value={email_templates.email01}
                  onChange={(v) => patchEmailTemplates({ email01: v })}
                  mode="nominative"
                  required
                />
                <EmailTemplateEditor
                  id="email02"
                  label="Email 2 — Relance"
                  value={email_templates.email02}
                  onChange={(v) => patchEmailTemplates({ email02: v })}
                  mode="nominative"
                />
                <EmailTemplateEditor
                  id="email03"
                  label="Email 3 — Dernier contact"
                  value={email_templates.email03}
                  onChange={(v) => patchEmailTemplates({ email03: v })}
                  mode="nominative"
                />
              </div>
            </TabsContent>

            <TabsContent value="fallback">
              <div className="space-y-6" data-testid="tab-fallback">
                <p className="text-xs text-gray-500">
                  Utilisé quand le prénom du contact est inconnu (boîte générique type
                  contact@company.com).
                </p>
                <EmailTemplateEditor
                  id="fallback_email01"
                  label="Fallback Email 1 — Premier contact"
                  value={email_templates.fallback_email01}
                  onChange={(v) => patchEmailTemplates({ fallback_email01: v })}
                  mode="fallback"
                />
                <EmailTemplateEditor
                  id="fallback_email02"
                  label="Fallback Email 2 — Relance"
                  value={email_templates.fallback_email02}
                  onChange={(v) => patchEmailTemplates({ fallback_email02: v })}
                  mode="fallback"
                />
                <EmailTemplateEditor
                  id="fallback_email03"
                  label="Fallback Email 3 — Dernier contact"
                  value={email_templates.fallback_email03}
                  onChange={(v) => patchEmailTemplates({ fallback_email03: v })}
                  mode="fallback"
                />
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          variant="secondary"
          onClick={() => setPreviewOpen(true)}
          disabled={!email_templates.email01.trim()}
          data-testid="preview-ia-btn"
        >
          <Sparkles size={16} />
          Voir un preview généré
        </Button>
      </div>

      <EmailPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        templates={email_templates}
        campaignContext={campaignContext}
      />
    </div>
  );
}
